import { useState } from 'react';
import { useAppStore } from '../store';
import { motion, AnimatePresence } from 'framer-motion';
import { FileEdit, Eye, EyeOff, CheckCircle, XCircle, Filter } from 'lucide-react';

const tierLabels: Record<string, { label: string; desc: string; color: string }> = {
  knowledge: { label: '基础 · 知识', desc: '回忆与理解基本概念', color: 'tier-knowledge' },
  application: { label: '应用 · 运用', desc: '在新情境中运用知识', color: 'tier-application' },
  analysis: { label: '分析 · 探究', desc: '分解信息并找出关联', color: 'tier-analysis' },
  evaluation: { label: '评估 · 综合', desc: '做出判断并提出论据', color: 'tier-evaluation' },
};

export default function ExercisesPage() {
  const { questions, activeSubjectId } = useAppStore();
  const subjectQuestions = questions.filter(q => q.subjectId === activeSubjectId);
  const [activeTier, setActiveTier] = useState<string>('all');
  const [showAnswers, setShowAnswers] = useState<Record<string, boolean>>({});

  const filtered = activeTier === 'all' ? subjectQuestions : subjectQuestions.filter(q => q.tier === activeTier);

  const toggleAnswer = (id: string) => setShowAnswers(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">练习册</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">按 IB 能力分层训练：基础 → 应用 → 分析 → 评估</p>
      </motion.div>

      {/* Tier filter */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="flex flex-wrap gap-2 scroll-x-mobile">
        <button
          onClick={() => setActiveTier('all')}
          className={`text-xs px-4 py-2 rounded-xl border transition-all font-body ${
            activeTier === 'all' ? 'bg-stone-950 text-white border-stone-950' : 'border-stone-200 hover:border-stone-300 text-stone-500'
          }`}
        >
          全部 ({subjectQuestions.length})
        </button>
        {Object.entries(tierLabels).map(([key, val]) => {
          const count = subjectQuestions.filter(q => q.tier === key).length;
          return (
            <button
              key={key}
              onClick={() => setActiveTier(key)}
              className={`text-xs px-4 py-2 rounded-xl border transition-all font-body ${
                activeTier === key ? `${val.color} border-current font-medium` : 'border-stone-200 hover:border-stone-300 text-stone-500'
              }`}
            >
              {val.label} ({count})
            </button>
          );
        })}
      </motion.div>

      {/* Questions */}
      <div className="space-y-4">
        {filtered.map((q, i) => {
          const tier = tierLabels[q.tier];
          const isOpen = showAnswers[q.id];
          return (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.05 }}
              className="card-premium overflow-hidden"
            >
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-[10px] px-2.5 py-0.5 rounded-lg font-medium font-body ${tier.color}`}>{tier.label}</span>
                  <span className="text-[10px] text-stone-400 font-body">{q.marks} 分</span>
                  <span className="text-[10px] px-2.5 py-0.5 rounded-lg bg-linen text-stone-500 font-body">
                    {q.type === 'multiple_choice' ? '选择题' : q.type === 'short_answer' ? '简答题' : q.type === 'structured' ? '结构题' : '论述题'}
                  </span>
                </div>
                <p className="text-sm text-stone-950 leading-relaxed whitespace-pre-line font-body">{q.content}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {q.knowledgePointIds.map(kpId => (
                    <span key={kpId} className="text-[9px] px-2 py-0.5 rounded-md bg-steel-50 text-steel font-body">知识点: {kpId}</span>
                  ))}
                </div>
              </div>

              <div className="border-t border-stone-100">
                <button
                  onClick={() => toggleAnswer(q.id)}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-3 text-xs hover:bg-linen/50 transition-colors active:bg-linen touch-target"
                >
                  <span className="flex items-center gap-2 text-sage-dark font-medium font-body">
                    {isOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {isOpen ? '隐藏答案' : '查看答案与评分标准'}
                  </span>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3"
                    >
                      <div className="bg-sage-50 rounded-xl p-4">
                        <p className="text-[10px] font-semibold text-sage-dark mb-1.5 flex items-center gap-1 font-body">
                          <CheckCircle className="w-3 h-3" /> 参考答案
                        </p>
                        <p className="text-xs text-stone-600 leading-relaxed font-body">{q.answer}</p>
                      </div>
                      <div className="bg-steel-50 rounded-xl p-4">
                        <p className="text-[10px] font-semibold text-steel mb-1.5 flex items-center gap-1 font-body">
                          <FileEdit className="w-3 h-3" /> 评分思路
                        </p>
                        <p className="text-xs text-stone-600 leading-relaxed font-body">{q.markScheme}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
